import { Field, connect as withFormik } from 'formik'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
// TODO Migrate all of console to i18next?  We have some dumb inner <a> tag stuff because of this lib
import { FormattedMessage, useIntl } from 'react-intl'
import { useQueryClient } from 'react-query'
import styled from 'styled-components'
import { string } from 'yup'

import Button from '@/components/Button'
import { Error, Link, Upload2 } from '@/components/Icons'
import { ImagePreview } from '@/components/ImagePreview'
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner'
import {
  fetchUploadRequestV2,
  requestDeleteAsset,
  uploadFileWithSignedUploadRequest
} from '@/utilities/assetUploadServices'

import ImageLibraryModal, { Image } from './ImageLibraryModal'
import messages from './messages'

//<editor-fold desc="Interfaces">
type ImageLibraryProps = {
  acceptedTypes?: Array<string>
  className?: string
  cancel?: (T: () => void) => void
  id?: string
  label?: string
  labelLocation?: 'before' | 'after'
  name: string
  placeholder?: string
  required?: boolean
  error?: string
  value?: string
  validate?: (callback: (T: any) => void) => void
  onError?: (error: string) => void
  onChange?: (url?: string) => void
  readOnly?: boolean
  customUrlComponent?: (T: CustomUrlComponentProps) => ReactNode
  useCustomUrlComponentPreview?: boolean
}

type ImageLibraryWithFormikProps = {
  formik: any
  shouldValidate?: boolean
}

type File = {
  type: string
  name: string
  size: number
}

type CustomUrlComponentProps = {
  id?: string
  name: string
  value?: string
  placeholder?: string
  validateUrlField?: (event: any) => Promise<void>
  setUrlField?: (T: any) => void
  error?: string
  reset: () => void
}
//</editor-fold>

//<editor-fold desc="Styled Components">
const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

// If we need to make the border look better, but we can't control the color very well
// background-image: url("data:image/svg+xml,<svg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'><rect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='darkblue' stroke-width='2' stroke-dasharray='6%2c 10' stroke-dashoffset='0' stroke-linecap='square'/></svg>");
const AssetDropzone: any = styled.div`
  background-color: white;
  border-width: 1px;
  border-color: var(--wg-gray-border);
  border-style: dashed;
  border-radius: 5px;
  min-height: 40px;
  padding: 0.25rem;
  width: 100%;
  align-items: center;
  color: var(--wg-blue-link);
  display: flex;
  flex-direction: row;
  font-size: 0.875rem;
  justify-content: center;

  &:hover {
    /* border: 1px dashed var(--wg-primary); */
    padding: rem-to-px(0.9375rem) - 1px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`

const UrlInputField: any = styled.input`
  flex: 1;
  background-color: #f5f8fa;
  border-width: 1px;
  border-style: solid;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  min-height: 40px;
  padding: 0.9375rem;

  border-color: ${({ isError }: any) => (isError ? 'var(--wg-red)' : 'var(--wg-gray-border)')};
`

const StyledUploadIcon = styled(Upload2)`
  height: 25px;
  margin: 0 0.25rem;
`

const StyledLinkIcon = styled(Link)`
  height: 0.79rem;
  margin-right: 8px;
`

const StyledErrorIcon: any = styled(Error)`
  height: 1.25rem;
`
const Label: any = styled.label`
  color: var(--wg-grey);
  font-size: 0.875rem;
  font-weight: bold;
  margin-bottom: 0.75rem;
  display: ${({ visible }: any) => (visible ? 'block' : 'none')};
`

const PreviewContainer: any = styled(Row)`
  min-height: 40px;
  border-radius: 5px;
  padding: 15px;
  background-color: ${({ readOnly }: any) => (readOnly ? '#F2F2F2' : '')};
`

const StyledImagePreview = styled(ImagePreview)`
  margin-right: 1rem;
  max-height: 40px;
`
//</editor-fold>

const isFromImageLibrary = (url?: string) => {
  return !!url?.match(/https:\/\/.*workgrid\.com\/assets\//)
}

const standardInputValidationSchema = string()
  .trim()
  // @ts-expect-error
  .url(<FormattedMessage {...messages.invalidUrl} />)
  .matches(
    /^$|(?:jpg|jpeg|gif|png)$/,
    // @ts-expect-error
    <FormattedMessage
      {...messages.invalidFileType}
      values={{
        types: 'jpg, jpeg, gif, png'
      }}
    />
  )

const ImageLibrary = ({
  acceptedTypes = ['png', 'gif', 'jpg', 'jpeg'],
  className,
  cancel,
  id,
  label,
  labelLocation = 'before',
  name,
  placeholder,
  required,
  error,
  value,
  validate = () => {},
  onError = () => {},
  onChange = () => {},
  readOnly = false,
  customUrlComponent,
  useCustomUrlComponentPreview = false
}: ImageLibraryProps) => {
  const intl = useIntl()

  const queryClient = useQueryClient()
  const [url, setUrl] = useState('')
  const [fileName, setFileName] = useState<string>()
  const [uploading, setUploading] = useState(false)
  const [internalError, setInternalError] = useState<any | undefined>()
  const [showingUrlField, setShowingUrlField] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const currentAssetId = useRef<string>()
  const cancelUpload = useRef<AbortController>()
  const attemptedFiles = useRef<Array<File>>()
  const {
    getRootProps,
    getInputProps,
    open: openFilePicker
  } = useDropzone({
    noClick: true,
    accept: acceptedTypes.map(a => `image/${a}`).join(', '),
    maxFiles: 1,
    onDrop: acceptedFiles => {
      // Hold on to these acceptedFiles just in case we need to retry
      attemptedFiles.current = acceptedFiles
      if (acceptedFiles?.length) {
        setFileName(acceptedFiles[0].name)
        setUrl(URL.createObjectURL ? URL.createObjectURL(acceptedFiles[0]) : '')
        requestUpload(acceptedFiles)
      } else {
        setInternalError(
          intl.formatMessage(messages.invalidUploadFileType, {
            types: acceptedTypes.join(', ')
          })
        )
        onError(
          intl.formatMessage(messages.invalidUploadFileType, {
            types: acceptedTypes.join(', ')
          })
        )
      }
    }
  })

  // Reflect any external set of value
  useEffect(() => {
    setInternalError(undefined)
    setUrl(value || '')
  }, [value])

  // A callback style function to allow a parent to say "cancel the entire form" and this input will delete
  // any image it has
  useEffect(() => {
    if (cancel) {
      // The whole "fire an event to a child"
      // Basically we call the function we are given, giving it a "callback" to run when needed
      cancel(reset)
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [cancel])

  useEffect(() => {
    // Setup the callback to run when anything external asks to validate this field
    // This is primarily used to prevent moving on while the file is uploading
    validate(async (val: string) => {
      if (internalError) {
        return internalError
      }

      if (uploading) {
        return intl.formatMessage(messages.uploading)
      } else if (required && !val) {
        // Bonus: check if the field is filled in if it's required (regardless if it's url entry or not)
        return intl.formatMessage(messages.requiredError, { label: label || '' })
      } else if (showingUrlField) {
        try {
          // Custom URL Components should manage their own validation
          if (customUrlComponent) return
          await standardInputValidationSchema.validate(val)
        } catch (validationError) {
          return (validationError as any).message
        }
      }
    })
  }, [internalError, intl, validate, showingUrlField, uploading, required, url, label, customUrlComponent])

  const requestUpload = async (acceptedFiles?: Array<File>) => {
    setInternalError(undefined)

    const files = acceptedFiles || attemptedFiles.current

    if (files?.length) {
      const file: File = files[0]
      setFileName(file.name)

      try {
        setUploading(true)
        const { postData, url, id: assetId }: any = await fetchUploadRequestV2(file)
        // Set the currentAssetId if we decide to remove this image (fires a delete)
        currentAssetId.current = assetId

        const { promise: fileUploadPromise, abortController } = uploadFileWithSignedUploadRequest(postData, file)
        // Save the ability to abort this upload
        cancelUpload.current = abortController

        fileUploadPromise
          .then(() => {
            attemptedFiles.current = undefined
            setInternalError(undefined)
            setUploading(false)
            queryClient.invalidateQueries({ queryKey: ['imageGallery.images'] })
            if (cancelUpload.current) {
              // If we haven't canceled
              onChange(url)
            }
          })
          .catch(() => {
            // Request to delete the item since it failed to upload
            requestDeleteAsset(assetId)
            // Catch any exceptions that come from the uploadFile to S3 step
            setInternalError(intl.formatMessage(messages.uploadFailure))
            onError(intl.formatMessage(messages.uploadFailure))
          })
      } catch {
        setInternalError(intl.formatMessage(messages.uploadFailure))
        onError(intl.formatMessage(messages.uploadFailure))
      }
    } else {
      // We were asked to upload but none of the files are of the correct type
      setInternalError(
        intl.formatMessage(messages.invalidUploadFileType, {
          types: acceptedTypes?.join(', ')
        })
      )
      onError(
        intl.formatMessage(messages.invalidUploadFileType, {
          types: acceptedTypes?.join(', ')
        })
      )
    }
  }

  const reset = () => {
    cancelUpload.current?.abort()
    cancelUpload.current = undefined

    // Removing a freshly uploaded asset is already too late, it's now in the gallery
    // So we do not call the "delete asset"
    if (currentAssetId.current) {
      currentAssetId.current = undefined
    }

    setInternalError(undefined)
    setFileName(undefined)
    setUrl('')
    setUploading(false)
    setShowingUrlField(false)
    onChange()
  }

  const setUrlField = async (event: any) => {
    setUrl(event?.target?.value || '')
    try {
      new URL(event?.target?.value)
      // Don't fire the onChange until it's a valid url
      onChange(event?.target?.value)
    } catch {
      // Nom nom nom
    }
  }

  const validateUrlField = async (event: any) => {
    const val = event?.target?.value
    try {
      const isValid = await standardInputValidationSchema.isValid(val)
      if (isValid) {
        setInternalError(undefined)
        onChange(val)
      } else {
        setInternalError(intl.formatMessage(messages.invalidUrl))
        onError(intl.formatMessage(messages.invalidUrl))
      }
    } catch {
      setInternalError(intl.formatMessage(messages.invalidUrl))
      onError(intl.formatMessage(messages.invalidUrl))
    }
  }

  const openModal = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    setIsModalOpen(true)
  }

  const onModalChange = (image: Image) => {
    setFileName(image?.fileName)
    setUrl(image?.url)
    setIsModalOpen(false)
    onChange(image?.url)
  }

  // Note, we have to render this regardless if it's being used because formik needs an actual input to run validations
  const renderUrlInput = (error?: string) => (
    <div style={{ display: showingUrlField ? 'block' : 'none' }}>
      {customUrlComponent ? (
        <>{customUrlComponent({ id, name, value, placeholder, validateUrlField, setUrlField, error, reset })}</>
      ) : (
        <>
          <Row>
            <StyledImagePreview src={url} alt={fileName || 'Preview'} />

            <UrlInputField
              id={id || name}
              value={url}
              placeholder={placeholder}
              onBlur={validateUrlField}
              onChange={setUrlField}
              isError={error || internalError}
              name={name}
            />
          </Row>
          <div className="mt-1 flex flex-row justify-between align-top">
            <div>{(error || internalError) && <p className="text-red-500">{error || internalError}</p>}</div>
            <Button color={'transparent'} size={'small'} onClick={reset}>
              <FormattedMessage {...messages.reset} />
            </Button>
          </div>
        </>
      )}
    </div>
  )

  const renderPreview = () =>
    url && !uploading && !isFromImageLibrary(url) && useCustomUrlComponentPreview && customUrlComponent ? (
      <>{customUrlComponent({ id, name, value, placeholder, validateUrlField, setUrlField, error, reset })}</>
    ) : (
      <>
        <PreviewContainer
          readOnly={readOnly}
          className="rounded-md border-0 bg-white text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300"
        >
          {url && <StyledImagePreview src={url} alt={intl.formatMessage(messages.preview)} />}
          {fileName && <p style={{ margin: 0 }}>{fileName}</p>}
          <span style={{ flex: 1 }} />
          {uploading ? (
            internalError ? (
              <StyledErrorIcon />
            ) : (
              <>
                <Button color={'transparent'} size={'small'} onClick={reset} style={{ paddingRight: 10 }}>
                  <FormattedMessage {...messages.cancel} />
                </Button>
                <LoadingSpinner />
              </>
            )
          ) : (
            !readOnly && (
              <Button color={'transparent'} size={'small'} onClick={reset}>
                {intl.formatMessage(messages.remove)}
              </Button>
            )
          )}
        </PreviewContainer>
        {(error || internalError) && renderRetry()}
      </>
    )

  const renderDropzone = () => (
    <AssetDropzone data-testid={'dropzone'} {...getRootProps()}>
      <input {...getInputProps()} />
      <StyledUploadIcon />
      <p>
        <Button color={'transparent'} size={'small'} onClick={openFilePicker}>
          {intl.formatMessage(messages.dropZoneUpload)}
        </Button>{' '}
        <FormattedMessage {...messages.dropZoneOr} /> {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a href={'#'} onClick={openModal}>
          {intl.formatMessage(messages.viewLibrary)}
        </a>
      </p>
    </AssetDropzone>
  )

  const renderRetry = () => (
    <Row>
      <p className="text-red-500">{error || internalError}</p>
      {internalError && (
        <>
          <span style={{ flex: 1 }} />
          <Button color={'transparent'} onClick={reset}>
            <FormattedMessage {...messages.reset} />
          </Button>
          <Button color={'primary'} onClick={() => requestUpload()}>
            <FormattedMessage {...messages.tryAgain} />
          </Button>
        </>
      )}
    </Row>
  )

  return (
    <>
      <div className={className} aria-hidden={isModalOpen}>
        {(!labelLocation || labelLocation !== 'after') && (
          <Label className="label" visible={!!label} htmlFor={id || name}>
            {label || intl.formatMessage(messages.defaultLabel)}
            {!!required && (
              <span className="ml-1 text-red-500">
                <FormattedMessage {...messages.requiredIcon} />
              </span>
            )}
          </Label>
        )}

        {!showingUrlField &&
          (uploading || url || readOnly ? (
            renderPreview()
          ) : (
            <>
              {renderDropzone()}
              <div className="mt-1 flex flex-row justify-between align-top">
                <div>{(error || internalError) && <p className="text-red-500">{internalError || error}</p>}</div>
                <Button color={'transparent'} size={'small'} onClick={() => setShowingUrlField(true)}>
                  <StyledLinkIcon />
                  <FormattedMessage {...messages.useImageUrl} />
                </Button>
              </div>
            </>
          ))}
        {!readOnly && renderUrlInput(error)}

        {!!labelLocation && labelLocation === 'after' && !!label && (
          <Label htmlFor={id || name}>
            {!!required && (
              <span className="ml-1 text-red-500">
                <FormattedMessage {...messages.requiredIcon} />
              </span>
            )}

            {!!label && label}
          </Label>
        )}
      </div>
      <ImageLibraryModal isOpen={isModalOpen} onChange={onModalChange} onCancel={() => setIsModalOpen(false)} />
    </>
  )
}

ImageLibrary.displayName = 'ImageLibrary'

const IntlImageLibrary = ImageLibrary

const ImageLibraryWithFormik = withFormik(
  ({
    id,
    name,
    required,
    label,
    formik,
    placeholder,
    className,
    readOnly,
    cancel,
    customUrlComponent,
    shouldValidate = true,
    useCustomUrlComponentPreview
  }: ImageLibraryProps & ImageLibraryWithFormikProps) => {
    const validateCallback = useRef<Function>()

    const setValidateCallback = (cb: Function) => {
      validateCallback.current = cb
    }

    const onChange = (val?: string) => {
      // We need this timeout because formik... is.. well "tricky"
      setTimeout(() => {
        formik.setFieldError(name, undefined)
        formik.setFieldValue(name, val)
      }, 0)
    }

    const onError = (err: string) => {
      formik.setFieldError(name, err)
    }

    const onValidate = async (value: string) => {
      return validateCallback.current && shouldValidate ? await validateCallback.current(value) : undefined
    }

    return (
      <Field name={id || name} validate={onValidate}>
        {({ field, form: { errors, touched } }: any) => (
          <IntlImageLibrary
            label={label}
            name={field.name}
            value={field.value}
            onChange={onChange}
            onError={onError}
            placeholder={placeholder}
            validate={setValidateCallback}
            required={required}
            error={touched[field.name] ? errors[field.name] : null}
            className={className}
            readOnly={readOnly}
            cancel={cancel}
            customUrlComponent={customUrlComponent}
            useCustomUrlComponentPreview={useCustomUrlComponentPreview}
          />
        )}
      </Field>
    )
  }
)

export default IntlImageLibrary
export { ImageLibraryWithFormik }
